import { sha256Hex } from "../cache/sha256.js";
import { KERNEL_MIGRATION_SQL } from "./migrations/001_kernel.sql.js";
import { SYNC_MIGRATION_SQL } from "./migrations/002_sync.sql.js";
import { IDENTITY_MIGRATION_SQL } from "./migrations/003_identity.sql.js";
import { REACTIVE_REPOSITORIES_MIGRATION_SQL } from "./migrations/004_reactive_repositories.sql.js";
import { OUTBOX_REPLAY_MIGRATION_SQL } from "./migrations/005_outbox_replay.sql.js";
import { CONVERSATIONS_MIGRATION_SQL } from "./migrations/006_conversations.sql.js";
import { REVISIONED_DOMAINS_MIGRATION_SQL } from "./migrations/007_revisioned_domains.sql.js";
import { WALLET_MIRROR_MIGRATION_SQL } from "./migrations/008_wallet_mirror.sql.js";
export const MIGRATIONS = [
    { name: "kernel", sql: KERNEL_MIGRATION_SQL, version: 1 },
    { name: "sync", sql: SYNC_MIGRATION_SQL, version: 2 },
    { name: "identity", sql: IDENTITY_MIGRATION_SQL, version: 3 },
    { name: "reactive-repositories", sql: REACTIVE_REPOSITORIES_MIGRATION_SQL, version: 4 },
    { name: "outbox-replay", sql: OUTBOX_REPLAY_MIGRATION_SQL, version: 5 },
    { name: "conversations", sql: CONVERSATIONS_MIGRATION_SQL, version: 6 },
    { name: "revisioned-domains", sql: REVISIONED_DOMAINS_MIGRATION_SQL, version: 7 },
    { name: "wallet-mirror", sql: WALLET_MIRROR_MIGRATION_SQL, version: 8 },
];
const CREATE_MIGRATIONS_TABLE = `
CREATE TABLE schema_migrations (
  version INTEGER PRIMARY KEY NOT NULL,
  name TEXT NOT NULL,
  checksum TEXT NOT NULL,
  applied_at TEXT NOT NULL
) STRICT;
`;
export class MigrationChecksumDriftError extends Error {
    version;
    constructor(version) {
        super(`Migration ${version} checksum does not match the applied schema`);
        this.version = version;
        this.name = "MigrationChecksumDriftError";
    }
}
export async function migrateDatabase(database, migrations, options = {}) {
    assertOrderedMigrations(migrations);
    const now = options.now ?? (() => new Date().toISOString());
    const checksums = await Promise.all(migrations.map(async (migration) => await sha256Hex(new TextEncoder().encode(migration.sql), options.subtle)));
    const hasMigrationsTable = database.selectValue("SELECT COUNT(*) FROM sqlite_master WHERE type = 'table' AND name = 'schema_migrations'") === 1;
    const appliedRows = hasMigrationsTable
        ? database.selectObjects("SELECT version, checksum FROM schema_migrations ORDER BY version")
        : [];
    for (const row of appliedRows) {
        const version = numberValue(row.version);
        const expected = checksums[version - 1];
        if (expected === undefined || row.checksum !== expected)
            throw new MigrationChecksumDriftError(version);
    }
    const applied = [];
    for (const migration of migrations) {
        if (appliedRows.some((row) => row.version === migration.version))
            continue;
        const checksum = checksums[migration.version - 1];
        database.transaction("IMMEDIATE", (transaction) => {
            if (migration.version === 1 && !hasMigrationsTable)
                transaction.exec(CREATE_MIGRATIONS_TABLE);
            transaction.exec(migration.sql);
            transaction.exec("INSERT INTO schema_migrations(version, name, checksum, applied_at) VALUES (?, ?, ?, ?)", { bind: [migration.version, migration.name, checksum, now()] });
        });
        applied.push(migration.version);
    }
    const version = Number(database.selectValue("SELECT COALESCE(MAX(version), 0) FROM schema_migrations") ?? 0);
    return { applied, version };
}
function assertOrderedMigrations(migrations) {
    migrations.forEach((migration, index) => {
        if (migration.version !== index + 1)
            throw new TypeError("SQLite migrations must be contiguous and ordered");
    });
}
function numberValue(value) {
    if (typeof value !== "number")
        throw new TypeError("Expected numeric migration version");
    return value;
}
//# sourceMappingURL=migrator.js.map